import axios from "axios";
import { toast } from "react-toastify";
import BASE_URL from "../../config/host";
import Cookies from "js-cookie";

const builderQueryString = (queries = [], contentType = "application/json") => {
  let queryString = "";
  if (queries) {
    Object.keys(queries).forEach((query) => {
      if (Array.isArray(queries[query]) && queries[query].length) {
        queries[query].forEach((item) => {
          queries[`${query}[]`] = item;
        });

        delete queries[query];
      }
    });

    queryString = new URLSearchParams(queries);
    queryString = "?" + queryString.toString();
  }

  return queryString;
};

function buildFormData(formData, data, parentKey) {
  if (
    data &&
    typeof data === "object" &&
    !(data instanceof Date) &&
    !(data instanceof File)
  ) {
    Object.keys(data).forEach((key) => {
      buildFormData(formData, data[key], parentKey ? `${parentKey}[${key}]` : key);
    });
  } else {
    const value = data == null ? "" : data;
    formData.append(parentKey, value);
  }
}

function jsonToFormData(data) {
  const formData = new FormData();
  buildFormData(formData, data);
  return formData;
}

// ---- Single-flight refresh (only one refresh at a time; others wait) ----
let refreshPromise = null;

const clearSessionAndRedirect = () => {
  localStorage.removeItem("auth_token");
  localStorage.removeItem("refreshToken");
  localStorage.removeItem("userDetails");
  localStorage.removeItem("appointmentData");
  localStorage.removeItem("joined");
  localStorage.removeItem("manualOfflineStatus");
  Cookies.remove("auth_token");
  window.location.href = "/signin";
};

/**
 * Shared refresh: concurrent 401s all await the same Promise.
 */
const refreshAccessToken = async () => {
  if (refreshPromise) return refreshPromise;

  refreshPromise = (async () => {
    const refreshToken = localStorage.getItem("refreshToken");
    if (!refreshToken) {
      throw new Error("No refresh token");
    }

    const res = await axios.post(`${BASE_URL}/refreshToken`, { refreshToken });
    const accessToken = res?.data?.accessToken;
    const newRefresh = res?.data?.refreshToken;

    if (!accessToken) {
      throw new Error("Refresh failed");
    }

    localStorage.setItem("auth_token", accessToken);
    if (newRefresh) localStorage.setItem("refreshToken", newRefresh);
    Cookies.set("auth_token", accessToken);

    return accessToken;
  })()
    .catch((err) => {
      clearSessionAndRedirect();
      throw err;
    })
    .finally(() => {
      refreshPromise = null;
    });

  return refreshPromise;
};

const isAuthFailure = (error) => {
  const status = error?.response?.status;
  const data = error?.response?.data;
  // Only hard-logout on genuine auth expiry / invalid token
  if (status === 401) {
    if (data?.tokenExpired || data?.tokenExpired === true) return "refreshable";
    if (
      typeof data?.message === "string" &&
      /token|session|unauthorized|jwt|expired|login again/i.test(data.message)
    ) {
      return "refreshable";
    }
    // Generic 401 — try refresh once if we have a refresh token
    if (localStorage.getItem("refreshToken")) return "refreshable";
    return "fatal";
  }
  return null;
};

export const apiRequest = async (
  method = "put",
  url = "",
  payload = { body: null, queries: null },
  contentType = "application/json",
  options = { useCustomUrl: false, showToastMessage: false },
) => {
  let defaultPayload = { body: null, queries: null };
  payload = { ...payload, defaultPayload };

  let defaultOptions = { useCustomUrl: false, showToastMessage: false };
  options = { ...options, defaultOptions };

  let queryString = "";
  if (payload.queries != null) {
    queryString = builderQueryString(payload?.queries, contentType);
  }

  const doRequest = async (accessToken) => {
    let headers = { "Content-Type": contentType };
    if (accessToken) {
      headers = { ...headers, Authorization: `Bearer ${accessToken}` };
    }

    let payloadData = payload.body;
    if (contentType.includes("form-data")) {
      payloadData = jsonToFormData(payload.body);
    }

    const request = axios.create({
      baseURL: options.useCustomUrl ? "" : BASE_URL,
      headers,
    });

    return request[method.toLowerCase()](
      `${url}${queryString}`,
      method === "delete" ? { data: payloadData } : payloadData,
    );
  };

  const accessToken =
    localStorage.getItem("auth_token") || Cookies.get("auth_token");

  try {
    let res = await doRequest(accessToken);

    // Axios only throws for error statuses by default — handle odd 401 in body
    if (res?.status === 401 && res?.data?.tokenExpired && localStorage.getItem("refreshToken")) {
      const newToken = await refreshAccessToken();
      res = await doRequest(newToken);
    }

    if (res.status) {
      if (options.showToastMessage) {
        if (res.data?.message) toast.success(res.data?.message);
        if (res.data?.data?.message) toast.success(res.data?.data?.message);
      }
      return res;
    }
    throw new Error(res?.message || res);
  } catch (error) {
    if (options.showToastMessage && error?.response?.data) {
      toast.error(
        typeof error.response.data === "string"
          ? error.response.data
          : error.response.data?.message || "Request failed",
      );
    }

    const status = error?.response?.status;

    // 403 = authenticated but not allowed — do NOT wipe session
    if (status === 403) {
      throw error.response || error;
    }

    const authKind = isAuthFailure(error);
    if (authKind === "refreshable") {
      try {
        const newToken = await refreshAccessToken();
        const retry = await doRequest(newToken);
        if (options.showToastMessage) {
          if (retry.data?.message) toast.success(retry.data?.message);
        }
        return retry;
      } catch (refreshErr) {
        // refreshAccessToken already redirects on failure
        throw refreshErr?.response || refreshErr;
      }
    }

    if (authKind === "fatal") {
      clearSessionAndRedirect();
    }

    throw error.response || error;
  }
};
